import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isAvailableNow } from '../utils/taskAvailability';
import { useAppContext } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { Task } from 'shared/types';
import TaskMemberModal from './TaskMemberModal';
import AntMascot from './AntMascot';
import { i18n } from '../i18n/i18n';
import { getSupabaseForApi } from '../services/supabaseClient';
import { KidCard, KidSectionHeading } from './kid/KidUI';
import { KidTaskCard, starsText } from './kid/KidTaskCard';
import { showToast } from './kid/Toast';
import { getRoom, getRoomName } from '../utils/roomName';
import { pluralKey } from '../utils/starGoal';
import { StoredPicture } from './kid/StoredPicture';

/**
 * Tasks (kid-first redesign step 5 — mock-ups phone-02 / tablet-*-02).
 * "Your tasks" (one "Let's go" each), "You can take these" ("I'll take it"), "Waiting for a
 * grown-up", other people's tasks (grown-ups only), and finished tasks collapsed to one row.
 * Sentence-case headings, friendly empty states, one action per card.
 */

const byNewest = (field: 'completed_at' | 'created_at') => (a: any, b: any) => {
  const t = (x: any) => {
    const v = x?.[field] || x?.created_at;
    return v instanceof Date ? v.getTime() : v ? new Date(v).getTime() : 0;
  };
  return t(b) - t(a);
};

const TaskBoard: React.FC = () => {
  const { current_user, current_family, claimTask, refreshFamilyData, loadMoreCompletedTasks } = useAppContext();
  const { current_language } = useLanguage();
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [startInFlow, setStartInFlow] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [claimingId, setClaimingId] = useState<string | null>(null);

  // Real-time: refresh when any family task changes
  useEffect(() => {
    if (!current_family?.id) return;
    // The signed-in user's own client: PIN members have a real session, so Realtime works for them.
    const rt = getSupabaseForApi();
    const channel = rt
      .channel(`tasks-family-${current_family.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks', filter: `family_id=eq.${current_family.id}` }, () => {
        refreshFamilyData().catch(() => {});
      })
      .subscribe();
    return () => {
      rt.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current_family?.id]);

  // Opened from the calendar with a task: show that task's details straight away (2026-10-12).
  const location = useLocation();
  const navigate = useNavigate();
  const openTaskId = (location.state as { openTaskId?: string } | null)?.openTaskId;
  useEffect(() => {
    if (!openTaskId || !current_family) return;
    const task = (current_family.tasks as any[]).find((t) => t.id === openTaskId);
    if (task) {
      setStartInFlow(false);
      setSelectedTask(task);
      setShowTaskModal(true);
    }
    navigate('.', { replace: true, state: null }); // a reload or Back doesn't reopen it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTaskId, current_family?.id]);

  if (!current_family || !current_user) {
    return <div className="p-8 text-center">{i18n.t('common.loading')}</div>;
  }

  const tasks = current_family.tasks as any[];
  // Repeat copies waiting for their day stay hidden (utils/taskAvailability).
  const mine = tasks.filter((t) => t.assigned_to === current_user.id && !t.is_completed && isAvailableNow(t));
  const available = tasks.filter((t) => !t.is_completed && !t.assigned_to && isAvailableNow(t));
  const waiting = tasks.filter((t) => t.is_completed && !t.verified && t.completed_by === current_user.id).sort(byNewest('completed_at'));
  const others = tasks.filter((t) => t.assigned_to && t.assigned_to !== current_user.id && !t.is_completed && isAvailableNow(t));
  const done = tasks.filter((t) => t.is_completed && t.verified).sort(byNewest('completed_at'));
  const doneTotal = current_family.completed_total ?? done.length;
  const nameOf = (id: string) => current_family.members.find((m: any) => m.id === id)?.name || '';
  const room = (t: any) => getRoomName(current_family.rooms, t.room_id);
  const roomImage = (t: any) => getRoom(current_family.rooms, t.room_id)?.image_url;

  const openTask = (task: Task, flow: boolean) => {
    setStartInFlow(flow);
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const handleTake = async (task: any) => {
    if (claimingId) return;
    setClaimingId(task.id);
    try {
      await claimTask(task.id);
      await refreshFamilyData();
      // Show where the card went (design: "every tap gets a visible response").
      showToast({ message: i18n.t('task_board.moved_to_yours').replace('{{task}}', task.title) });
      document.getElementById('your-tasks')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error: any) {
      showToast({ message: error?.message || 'Could not take the task', tone: 'error' });
    } finally {
      setClaimingId(null);
    }
  };

  const emptyCard = (text: string) => (
    <KidCard tone="mint" className="flex items-center gap-4 !py-4">
      <AntMascot pose="wave" className="w-14 h-14 shrink-0" />
      <p className="font-bold text-lg">{text}</p>
    </KidCard>
  );

  return (
    <div className="w-full max-w-[1180px] mx-auto px-4 md:px-8 py-4 md:py-7 space-y-7 text-kid-text dark:text-kid-text-dark" lang={current_language}>
      <h1 className="font-kid-heading font-extrabold text-3xl md:text-4xl">{i18n.t('task_board.title')}</h1>

      {/* phone/portrait: one column of sections; ≥1024 px: three columns like mock-up tablet-landscape-02 */}
      <div className="space-y-7 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-6 lg:items-start">
      <section id="your-tasks" className="scroll-mt-24 space-y-3">
        <KidSectionHeading>{i18n.t('task_board.your_tasks')}</KidSectionHeading>
        {mine.length === 0 ? (
          emptyCard(i18n.t('task_board.no_your_tasks'))
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4">
            {mine.map((t) => (
              <KidTaskCard
                key={t.id}
                task={t}
                roomName={room(t)}
                roomImage={roomImage(t)}
                actionLabel={i18n.t('task_flow.start_task')}
                actionTrailing={<span aria-hidden>→</span>}
                onAction={() => openTask(t, true)}
              />
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <KidSectionHeading>{i18n.t('task_board.can_take')}</KidSectionHeading>
        {available.length === 0 ? (
          emptyCard(i18n.t('task_board.nothing_to_take'))
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4">
            {available.map((t) => (
              <KidTaskCard
                key={t.id}
                task={t}
                roomName={room(t)}
                roomImage={roomImage(t)}
                actionLabel={i18n.t('task_board.take_task')}
                actionVariant="secondary"
                busy={claimingId === t.id}
                onAction={() => handleTake(t)}
                linkLabel={i18n.t('task_flow.see_details')}
                onLink={() => openTask(t, false)}
              />
            ))}
          </div>
        )}
      </section>

      <div className="space-y-7">
      {waiting.length > 0 && (
        <section className="space-y-3">
          <KidSectionHeading>{i18n.t('task_board.waiting_heading')}</KidSectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-3">
            {waiting.map((t) => (
              <KidCard key={t.id} tone="peach" className="flex items-center gap-4 !py-4">
                <span className="text-2xl" aria-hidden>⌛</span>
                <div className="min-w-0">
                  <p className="font-bold text-lg leading-tight truncate">{t.title}</p>
                  <p className="text-base">{i18n.t(pluralKey('dashboard.waiting', t.points)).replace('{{n}}', String(t.points))}</p>
                </div>
              </KidCard>
            ))}
          </div>
        </section>
      )}

      {/* Grown-ups see what everyone else is doing */}
      {current_user.is_admin && others.length > 0 && (
        <section className="space-y-3">
          <KidSectionHeading>{i18n.t('task_board.others_tasks')}</KidSectionHeading>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-2">
            {others.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => openTask(t, false)}
                  className="w-full min-h-[56px] flex items-center gap-3 px-4 py-2 rounded-kid-tile bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border dark:border-kid-border-dark text-left"
                >
                  <span className="flex-1 min-w-0">
                    <span className="block font-bold truncate">{t.title}</span>
                    <span className="block text-sm">{nameOf(t.assigned_to)} · {starsText(Number(t.points) || 0)}</span>
                  </span>
                  <span aria-hidden>›</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {doneTotal > 0 && (
        <section>
          <button
            type="button"
            onClick={() => setShowDone((v) => !v)}
            aria-expanded={showDone}
            className="w-full min-h-[56px] flex items-center gap-3 px-4 rounded-kid-card bg-kid-bg dark:bg-kid-bg-dark font-bold text-lg text-left"
          >
            <span className="w-7 h-7 rounded-full border-2 border-kid-green-shadow dark:border-green-300 text-kid-green-shadow dark:text-green-300 flex items-center justify-center text-sm" aria-hidden>✓</span>
            <span className="flex-1">{i18n.t(pluralKey('task_board.done', doneTotal)).replace('{{n}}', String(doneTotal))}</span>
            <span className="text-kid-green-shadow dark:text-green-300">
              {showDone ? i18n.t('rewards.show_fewer') : i18n.t('rewards.see_all')} <span aria-hidden>{showDone ? '▲' : '›'}</span>
            </span>
          </button>
          {showDone && (
            <div className="mt-2 flex flex-col gap-2">
              {done.map((t) => (
                <div key={t.id} className="flex items-center gap-3 px-3 py-2 rounded-kid-tile bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border dark:border-kid-border-dark">
                  <StoredPicture url={t.header_image_url} imageId={t.header_image} label={t.title} size={44} fit="cover" />
                  <span className="flex-1 min-w-0">
                    <span className="block font-bold truncate">{t.title}</span>
                    <span className="block text-sm">
                      {t.verified ? `✓ ${starsText(Number(t.points) || 0)}` : i18n.t('task_board.waiting_heading')}
                      {t.completed_by && t.completed_by !== current_user.id && ` · ${nameOf(t.completed_by)}`}
                    </span>
                  </span>
                </div>
              ))}
              {/* Older finished tasks aren't loaded up front (bandwidth) — fetch them on request. */}
              {current_family.completed_has_more && (
                <button
                  type="button"
                  disabled={loadingOlder}
                  className="self-center min-h-[44px] px-3 font-bold underline text-kid-green-shadow dark:text-green-300 disabled:opacity-50"
                  onClick={async () => {
                    setLoadingOlder(true);
                    try { await loadMoreCompletedTasks(); } finally { setLoadingOlder(false); }
                  }}
                >
                  {loadingOlder ? '…' : i18n.t('admin_panel.show_more_completed_tasks')}
                </button>
              )}
            </div>
          )}
        </section>
      )}
      </div>
      </div>

      <TaskMemberModal
        task={selectedTask}
        open={showTaskModal}
        startInFlow={startInFlow}
        onClose={() => {
          setShowTaskModal(false);
          setSelectedTask(null);
          void refreshFamilyData();
        }}
      />
    </div>
  );
};

export default TaskBoard;
